import { UtensilsCrossed, Croissant, Coffee, Pizza, Beef } from 'lucide-react';

/**
 * Sectores del ramo alimenticio a los que sirve el mismo catálogo.
 *
 * Por qué existe este archivo: el sitio entero estaba escrito para
 * tortillerías y taquerías (título de Home incluido). Es la posición más
 * fuerte y no se toca — pero los mismos productos resuelven el problema de
 * una repostería, una cafetería o una pizzería por razones distintas, y sin
 * una página propia esas búsquedas no tenían dónde aterrizar.
 *
 * REGLA DE HONESTIDAD (la misma de highlights.js y de las guías): un sector
 * se vende con atributos REALES del catálogo — gramaje, tratamiento
 * antigrasa, medida, presentación — nunca con un producto que no existe.
 * Carpepel no vende cajas de pizza, así que la página de pizzerías habla del
 * papel que forra la charola, no de la caja. Si algún día se agrega un
 * producto nuevo, la clasificación de abajo lo recoge sola.
 *
 * `matches(product)` clasifica por gramaje y categoría en vez de por una
 * lista fija de IDs, por dos razones: (1) un producto nuevo entra solo al
 * sector correcto sin editar este archivo, y (2) el gramaje ES el criterio
 * técnico que el propio sitio ya enseña en la guía "cómo elegir el gramaje
 * correcto" — 16-18g para ligeros y secos, 24-30g para uso general, 40-49g
 * para pesado, húmedo o muy grasoso. Las páginas de sector y las guías dicen
 * lo mismo porque leen la misma regla.
 */

const esAntigrasa = (p) => p.category === 'papel_snacks';
const esBolsa = (p) => p.category === 'bolsas_poliseda' || p.category === 'Plásticos';
const gramaje = (p) => p.weight_grams || 0;

export const SECTORS = [
    {
        slug: 'restaurantes',
        label: 'Restaurantes',
        Icon: UtensilsCrossed,
        // Gancho de una línea para la tarjeta del selector en Home.
        hook: 'Antigrasa para fritura, carnes y alto volumen',
        eyebrow: 'Papel para restaurantes',
        h1: 'Papel grado alimenticio para restaurantes',
        lead: 'Un restaurante que maneja fritura y carnes tiene un problema distinto al de una panadería: el papel no solo envuelve, tiene que aguantar el aceite sin transparentarse, romperse ni pasar olor a la comida.',
        // Cada punto se apoya en un atributo verificable del catálogo.
        points: [
            'Gramajes altos (29g a 49g) para producto pesado, húmedo o con grasa visible.',
            'Papel antigrasa para snacks en medida 30×30, pensado para hamburguesas, hot dogs y frituras.',
            'Presentaciones en paca y en pliego grande (56×76) para consumo de alto volumen.',
        ],
        matches: (p) => esAntigrasa(p) || gramaje(p) >= 29,
        faq: [
            {
                q: '¿Qué papel antigrasa conviene a un restaurante?',
                a: 'Para grasa visible —fritura, carnes, hamburguesas— conviene papel con tratamiento antigrasa o de gramaje alto. En el catálogo de Carpepel eso corresponde al papel para snacks (30g, medida 30×30) y al papel revolución (49g), que está pensado para alimentos pesados, húmedos o muy grasosos. El papel estraza común, sin tratamiento, no está diseñado para ese uso aunque muchos negocios lo usen por costumbre.',
            },
            {
                q: '¿Qué gramaje necesita un restaurante de alto volumen?',
                a: 'De 29g en adelante para uso general con grasa, y 49g (papel revolución) para producto pesado o muy grasoso. El papel revolución se vende en paca de 25 kg y en pliego de 56×76 cm, la presentación habitual para cocinas de alto volumen.',
            },
            {
                q: '¿Carpepel vende papel para restaurantes al mayoreo?',
                a: 'Sí. Todo el catálogo se vende al mayoreo y al menudeo, con precios especiales por volumen, y con envío a toda la República Mexicana.',
            },
        ],
        relatedGuides: ['papel-antigrasa-restaurantes', 'como-elegir-el-gramaje-correcto'],
        seo: {
            title: 'Papel Antigrasa para Restaurantes al Mayoreo | Carpepel',
            description: 'Papel grado alimenticio para restaurantes: antigrasa para fritura y carnes, gramajes de 29g a 49g y pliego grande. Mayoreo y menudeo con envío a todo México.',
            keywords: [
                'papel para restaurantes mayoreo',
                'papel antigrasa para restaurantes',
                'papel grado alimenticio restaurantes',
                'papel para fritura',
                'papel para hamburguesas',
                'proveedor de papel para restaurantes México',
            ],
        },
    },
    {
        slug: 'reposterias',
        label: 'Panaderías y reposterías',
        Icon: Croissant,
        hook: 'Tissue e interfoliado para pan, pasteles y galletas',
        eyebrow: 'Papel para panaderías y reposterías',
        h1: 'Papel grado alimenticio para panaderías y reposterías',
        lead: 'El pan y la repostería no necesitan barrera contra la grasa: necesitan ligereza, que el papel no se pegue al producto y que no le quite frescura. Es el caso contrario al de una cocina de fritura.',
        points: [
            'Papel tissue de 24g con alta absorción, usado como interfoliado entre piezas de pan y repostería.',
            'Papel cebolla de 18g, ligero y transparente, para pan y productos secos.',
            'Bolsas de poliseda grado alimenticio por rollo, para pan y producto a granel.',
        ],
        matches: (p) => esBolsa(p) || (gramaje(p) > 0 && gramaje(p) <= 24),
        faq: [
            {
                q: '¿Qué papel se usa para interfoliar pan y repostería?',
                a: 'Papel tissue grado alimenticio de bajo gramaje. Carpepel maneja tissue café de 24g en medidas 30×40, 34×45 y 50×70 cm, ligero y con buena absorción, que es lo que se usa como interfoliado entre piezas de pan y repostería para que no se peguen entre sí.',
            },
            {
                q: '¿Qué papel conviene para envolver pan?',
                a: 'Para pan y productos secos conviene papel ligero sin tratamiento de barrera: papel cebolla de 18g o tissue de 24g. El pan no tiene grasa visible, así que no necesita papel antigrasa — pagar por una barrera que no se usa solo encarece el empaque.',
            },
            {
                q: '¿Carpepel vende bolsas para panadería?',
                a: 'Sí, bolsas de poliseda grado alimenticio en rollo, en medidas 20×30, 25×35 y 30×40 cm, aptas para pan y producto a granel, y bolsas de asa (camiseta) para entregar el pedido.',
            },
        ],
        relatedGuides: ['como-elegir-el-gramaje-correcto', 'papel-encerado-vs-estraza'],
        seo: {
            title: 'Papel para Panaderías y Reposterías al Mayoreo | Carpepel',
            description: 'Papel grado alimenticio para panaderías y reposterías: tissue de 24g para interfoliar, papel cebolla de 18g y bolsas de poliseda. Mayoreo con envío a México.',
            keywords: [
                'papel para panaderia mayoreo',
                'papel para reposteria',
                'papel interfoliado para pan',
                'papel tissue para pan',
                'bolsas para panaderia mayoreo',
                'papel grado alimenticio panaderia',
            ],
        },
    },
    {
        slug: 'cafeterias',
        label: 'Cafeterías',
        Icon: Coffee,
        hook: 'Envolver sándwiches, panqués y pedidos para llevar',
        eyebrow: 'Papel para cafeterías',
        h1: 'Papel grado alimenticio para cafeterías',
        lead: 'En una cafetería el papel se ve. El sándwich, el panqué o el wrap se entregan envueltos sobre la mesa o en la bolsa para llevar, así que el empaque es parte de la presentación — y aun así tiene que aguantar mantequilla, aderezo y calor.',
        points: [
            'Gramajes de uso general (24g a 30g) para sándwiches, wraps y panqués.',
            'Papel para snacks en cinco diseños, medida 30×30, cuando el empaque es parte de la presentación.',
            'Bolsas de asa para el pedido para llevar.',
        ],
        matches: (p) => esAntigrasa(p) || esBolsa(p) || (gramaje(p) >= 24 && gramaje(p) <= 30),
        faq: [
            {
                q: '¿Qué papel se usa para envolver sándwiches?',
                a: 'Papel grado alimenticio de gramaje medio, de 24g a 30g. Si el sándwich lleva aderezo o mantequilla conviene el papel para snacks (30g, con tratamiento antigrasa); si es seco, basta un papel cebolla o tissue de 24g.',
            },
            {
                q: '¿Qué papel resiste la mantequilla y el aderezo?',
                a: 'El papel con tratamiento antigrasa. En el catálogo de Carpepel es el papel para snacks de 30g en medida 30×30 cm, disponible en cinco diseños, pensado justamente para producto con grasa visible como hamburguesas, hot dogs y antojitos.',
            },
            {
                q: '¿Carpepel vende bolsas para pedidos para llevar?',
                a: 'Sí, bolsas de asa (camiseta) por bulto de 25 kg en cuatro tamaños, y bolsas de poliseda en rollo grado alimenticio en tres medidas.',
            },
        ],
        relatedGuides: ['como-elegir-el-gramaje-correcto', 'que-es-el-papel-grado-alimenticio'],
        seo: {
            title: 'Papel para Cafeterías al Mayoreo | Carpepel',
            description: 'Papel grado alimenticio para cafeterías: envuelve sándwiches, wraps y panqués con papel antigrasa en cinco diseños. Mayoreo y menudeo con envío a México.',
            keywords: [
                'papel para cafeteria mayoreo',
                'papel para envolver sandwiches',
                'papel antigrasa para cafeteria',
                'papel grado alimenticio cafeteria',
                'bolsas para cafeteria',
            ],
        },
    },
    {
        slug: 'pizzerias',
        label: 'Pizzerías',
        Icon: Pizza,
        hook: 'Forro antigrasa para charola y caja',
        eyebrow: 'Papel para pizzerías',
        h1: 'Papel antigrasa para pizzerías',
        lead: 'El problema de una pizzería no es la caja: es el aceite que la traspasa. Un forro antigrasa entre la pizza y el cartón evita que la base se empape, que la caja se manche y que la rebanada se pegue al fondo.',
        // Se dice explícitamente lo que NO se vende, para que ni el cliente ni
        // un motor de IA infiera que Carpepel surte cajas de pizza.
        points: [
            'Papel revolución de 49g y pliego de 56×76, la presentación para producto pesado y muy grasoso.',
            'Papel para snacks de 30g en medida 30×30, que entra en charola sin recortar.',
            'Carpepel no vende cajas de pizza: vende el papel que las forra.',
        ],
        matches: (p) => esAntigrasa(p) || gramaje(p) >= 30,
        faq: [
            {
                q: '¿Qué papel se pone debajo de la pizza en la caja?',
                a: 'Un forro de papel antigrasa o de gramaje alto, que actúa como barrera entre la pizza y el cartón. En el catálogo de Carpepel corresponde al papel para snacks (30g, medida 30×30 cm) y al papel revolución (49g, disponible en pliego de 56×76 cm), que está pensado para alimentos pesados, húmedos o muy grasosos.',
            },
            {
                q: '¿Carpepel vende cajas de pizza?',
                a: 'No. Carpepel vende papel grado alimenticio e insumos para empaque de alimentos, incluido el papel antigrasa que forra la charola o la caja, pero no la caja de cartón en sí.',
            },
            {
                q: '¿Qué gramaje aguanta el aceite de la pizza sin transparentarse?',
                a: 'De 30g en adelante, y 49g (papel revolución) para el caso más exigente. Por debajo de 24g el papel está pensado para producto seco y ligero, no para grasa visible.',
            },
        ],
        relatedGuides: ['papel-antigrasa-restaurantes', 'como-elegir-el-gramaje-correcto'],
        seo: {
            title: 'Papel Antigrasa para Pizzerías al Mayoreo | Carpepel',
            description: 'Papel antigrasa para pizzerías: forro de charola y caja en 30g y papel revolución de 49g. Evita que el aceite traspase el cartón. Mayoreo con envío a México.',
            keywords: [
                'papel para pizzeria mayoreo',
                'papel antigrasa para pizza',
                'papel para caja de pizza',
                'forro de caja de pizza',
                'papel grado alimenticio pizzeria',
            ],
        },
    },
    {
        slug: 'taquerias',
        label: 'Taquerías y puestos',
        Icon: Beef,
        hook: 'Uso general para tacos, tortas y antojitos',
        eyebrow: 'Papel para taquerías y puestos',
        h1: 'Papel grado alimenticio para taquerías y puestos',
        lead: 'Es el uso que más papel consume del país y el que menos margen de error tiene: el taco sale caliente y grasoso, y el papel tiene que aguantar desde la plancha hasta la mano del cliente.',
        points: [
            'Gramajes de uso general (24g a 30g) en varios colores: canela, amarillo, mostaza y oro.',
            'Medidas de 34×45 a 50×70 cm según el tamaño de la porción.',
            'Presentación en paca, la forma habitual de compra por volumen del sector.',
        ],
        matches: (p) => gramaje(p) >= 24 && gramaje(p) <= 30,
        faq: [
            {
                q: '¿Qué papel se usa para envolver tacos?',
                a: 'Papel grado alimenticio de uso general, entre 24g y 30g. Carpepel maneja papel canela (30g), amarillo (29g), mostaza (29g) y oro (29g) en medidas de 34×45 a 50×70 cm, todos aptos para tacos, tortas y antojitos.',
            },
            {
                q: '¿Cuánto papel necesita una taquería al mes?',
                a: 'Se calcula multiplicando las piezas vendidas en un día promedio por las hojas que usa cada pieza, y ese resultado por los días que abre al mes. El punto de reorden debe cubrir al menos el tiempo de entrega del proveedor más un colchón de seguridad.',
            },
            {
                q: '¿Cuál es la diferencia entre el papel oro, amarillo y mostaza?',
                a: 'Prácticamente solo el color y la medida: los tres son papel grado alimenticio de 29g. El oro viene en 34×46, el mostaza en 35×50 y el amarillo en tres medidas (34×45, 35×50 y 50×70). Se eligen por presentación y por la medida que mejor ajusta a la porción.',
            },
        ],
        relatedGuides: ['cuanto-papel-necesita-tu-negocio', 'como-elegir-el-gramaje-correcto'],
        seo: {
            title: 'Papel para Taquerías y Puestos al Mayoreo | Carpepel',
            description: 'Papel grado alimenticio para taquerías y puestos: canela, amarillo, mostaza y oro de 24g a 30g, en paca y varias medidas. Mayoreo con envío a México.',
            keywords: [
                'papel para taqueria mayoreo',
                'papel para envolver tacos',
                'papel para puestos de comida',
                'papel grado alimenticio taqueria',
                'papel canela mayoreo',
            ],
        },
    },
];

export const SECTOR_SLUGS = SECTORS.map((s) => s.slug);

/**
 * Rango de gramaje real de los productos que caen en un sector.
 *
 * Se calcula del catálogo, NO se escribe a mano en la tabla de arriba: el
 * gramaje que enseña la tarjeta tiene que ser el de los productos que la
 * página realmente lista, o sería una cifra decorativa que se desincroniza
 * en cuanto el admin suba o dé de baja un producto. Mismo criterio que
 * highlights.js — un dato derivado del backend, no una promesa fija.
 *
 * Las bolsas (poliseda, plásticos) no tienen gramaje de papel y se excluyen
 * del rango en vez de contarlas como 0, que hundiría el mínimo a cero.
 * Devuelve null si el sector no tiene ningún producto con gramaje —el
 * consumidor decide qué hacer— en vez de un rango inventado.
 */
export function gramajeRange(sector, products) {
    const pesos = productsForSector(sector, products)
        .map((p) => p.weight_grams)
        .filter((g) => typeof g === 'number' && g > 0);
    if (pesos.length === 0) return null;
    const min = Math.min(...pesos);
    const max = Math.max(...pesos);
    return { min, max, label: min === max ? `${min} g` : `${min}–${max} g` };
}

export function getSector(slug) {
    return SECTORS.find((s) => s.slug === slug);
}

/** Productos del catálogo que corresponden a un sector, en el orden del catálogo. */
export function productsForSector(sector, products) {
    if (!sector || !Array.isArray(products)) return [];
    return products.filter((p) => {
        try {
            return sector.matches(p);
        } catch {
            return false;
        }
    });
}
